import { appInfo, aboutInfo, carouselItems, indexItems, commentItems } from '@/mocks/homeData';

/* ===== Section: App ===== */
export function AppSection() {
  return (
    <section id="app" className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[960px] mx-auto px-4 md:px-6">
        <div className="bg-gradient-to-br from-primary-50 via-background-50 to-accent-50 rounded-lg p-8 md:p-12 flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">
              {appInfo.title}
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
              {appInfo.description}
            </p>
            <div className="flex flex-wrap gap-2 justify-center lg:justify-start mb-6">
              {appInfo.features.map((feat, idx) => (
                <span key={idx} className="px-3 py-1 text-xs bg-background-50 text-foreground-600 rounded-full border border-background-200 whitespace-nowrap">
                  {feat}
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
              <a
                href="#app"
                onClick={(e) => e.preventDefault()}
                className="px-6 py-3 bg-foreground-900 hover:bg-foreground-800 text-background-50 text-sm rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-android-line mr-2"></i>安卓下载
              </a>
              <a
                href="#app"
                onClick={(e) => e.preventDefault()}
                className="px-6 py-3 bg-background-50 hover:bg-background-100 text-foreground-700 text-sm rounded-md border border-background-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-apple-line mr-2"></i>iOS下载
              </a>
            </div>
          </div>
          <div className="flex-shrink-0 w-48 h-48 md:w-56 md:h-56 rounded-2xl bg-primary-500/10 flex items-center justify-center">
            <span className="w-24 h-24 md:w-28 md:h-28 rounded-2xl bg-primary-500 flex items-center justify-center">
              <i className="ri-smartphone-line text-background-50 text-4xl md:text-5xl"></i>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== Section: About ===== */
export function AboutSection() {
  return (
    <section id="about" className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[960px] mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-10">
          关于我们
        </h2>
        <div className="bg-background-50 rounded-lg p-6 md:p-10">
          <div className="space-y-4 mb-8">
            {aboutInfo.paragraphs.map((text, idx) => (
              <p key={idx} className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {text}
              </p>
            ))}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {aboutInfo.stats.map((stat, idx) => (
              <div key={idx} className="text-center p-4 rounded-lg bg-background-100">
                <p className="text-2xl md:text-3xl font-heading text-primary-500 mb-1">{stat.value}</p>
                <p className="text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== Section: Carousel ===== */
export function CarouselSection() {
  return (
    <section id="carousel" className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-10">
          焦点关注
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
          {carouselItems.map((item, idx) => (
            <div
              key={idx}
              className="bg-background-100 rounded-lg overflow-hidden group cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
            >
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
                <img
                  src={item.image}
                  alt={item.title}
                  title={item.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-0.5 text-xs bg-accent-500 text-background-50 rounded whitespace-nowrap">
                    {item.tag}
                  </span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-base md:text-lg text-foreground-800 mb-2 leading-snug">
                  {item.title}
                </h3>
                <p className="text-xs md:text-sm text-foreground-500 leading-relaxed">
                  {item.summary}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===== Section: Index ===== */
export function IndexSection() {
  return (
    <section id="index" className="py-14 md:py-20 bg-background-100">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-10">
          热门专题
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {indexItems.map((item, idx) => (
            <a
              key={idx}
              href="#cards"
              onClick={(e) => e.preventDefault()}
              className="bg-background-50 rounded-lg p-5 flex items-start gap-4 cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
            >
              <span className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <i className={`${item.icon} text-primary-500 text-xl`}></i>
              </span>
              <div className="min-w-0">
                <h3 className="text-sm md:text-base text-foreground-800 mb-1 whitespace-nowrap">
                  {item.title}
                </h3>
                <p className="text-xs text-foreground-500 leading-relaxed mb-2">
                  {item.description}
                </p>
                <span className="text-xs text-foreground-400">{item.count} 篇</span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ===== Section: Comments ===== */
export function CommentsSection() {
  return (
    <section id="comments" className="py-14 md:py-20 bg-background-50">
      <div className="max-w-[960px] mx-auto px-4 md:px-6">
        <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 text-center mb-10">
          瓜友热评
        </h2>
        <div className="space-y-4">
          {commentItems.map((item, idx) => (
            <div key={idx} className="bg-background-100 rounded-lg p-5 flex gap-4">
              <div className="w-10 h-10 md:w-11 md:h-11 rounded-full overflow-hidden flex-shrink-0 bg-background-200">
                <img
                  src={item.avatar}
                  alt={item.author}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-sm text-foreground-800">{item.author}</span>
                  <span className="text-xs text-foreground-400">{item.time}</span>
                </div>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {item.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}